// ─── Shared Types for Behavior Timeline Charts ─────────────────────────────────

export type BehaviorType = 'spin' | 'scratch' | 'walk' | 'standing' | 'sitting' | 'lying' | 'rest' | 'toilet' | 'unknown';
export type Severity = 'normal' | 'elevated' | 'alert';

// Alert behavior types — used by AlertData and AlertLogEntry
export type AlertBehaviorType = 'spin' | 'toilet' | 'rest' | 'activity' | 'scratch' | 'lying' | 'sitting' | 'standing' | 'walk';

export interface GanttBarData {
  id: string;
  type: BehaviorType;
  startTime: number; // epoch ms
  endTime: number;   // epoch ms
  severity: Severity;
  confidence: number;
  poseCount?: number; // number of individual pose detections in this segment
}

// ─── Dog State ↔ Behavior Type Mapping ────────────────────────────────────────
// The model outputs: sit, stand, lie, walk, scratch, unsure
// The UI uses:      sitting, standing, lying, walk, pacing, unknown
// Derived behaviors (spin, rest, toilet) have no direct dog_state mapping.

export const DOG_STATE_TO_BEHAVIOR: Record<string, BehaviorType> = {
  sit: 'sitting',
  stand: 'standing',
  lie: 'lying',
  walk: 'walk',
  scratch: 'scratch',
  unsure: 'unknown',
};

export const BEHAVIOR_TO_DOG_STATE: Record<string, string> = {};
for (const [ds, bt] of Object.entries(DOG_STATE_TO_BEHAVIOR)) {
  BEHAVIOR_TO_DOG_STATE[bt] = ds;
}

export const DOG_STATE_LABELS: Record<string, string> = {
  sit: 'Sitting',
  stand: 'Standing',
  lie: 'Lying',
  walk: 'Walking',
  scratch: 'Scratching',
  unsure: 'Unknown',
};

export const DOG_STATE_COLORS: Record<string, string> = {
  sit: '#16a34a',
  stand: '#22c55e',
  lie: '#6b7280',
  walk: '#3b82f6',
  scratch: '#f97316',
  unsure: '#a855f7',
};

export function dogStateToBehaviorType(dogState: string): BehaviorType {
  return DOG_STATE_TO_BEHAVIOR[dogState] ?? 'unknown';
}

export function behaviorTypeToDogState(behavior: BehaviorType): string | undefined {
  return BEHAVIOR_TO_DOG_STATE[behavior];
}

export const ROW_ORDER: BehaviorType[] = [
  'spin', 'scratch', 'walk', 'standing', 'sitting', 'lying', 'rest', 'toilet', 'unknown',
];

export const BEHAVIOR_LABELS: Record<BehaviorType, string> = {
  spin: 'Spinning', scratch: 'Scratching', walk: 'Walking', standing: 'Standing',
  sitting: 'Sitting', lying: 'Lying', rest: 'Resting', toilet: 'Toilet', unknown: 'Unknown',
};

export const BEHAVIOR_COLORS: Record<BehaviorType, { normal: string; elevated: string; alert: string }> = {
  spin:     { normal: '#a855f7', elevated: '#c084fc', alert: '#ef4444' },
  scratch:  { normal: '#f97316', elevated: '#fb923c', alert: '#ef4444' },
  walk:     { normal: '#3b82f6', elevated: '#60a5fa', alert: '#ef4444' },
  standing: { normal: '#22c55e', elevated: '#4ade80', alert: '#ef4444' },
  sitting:  { normal: '#16a34a', elevated: '#22c55e', alert: '#ef4444' },
  lying:    { normal: '#6b7280', elevated: '#9ca3af', alert: '#ef4444' },
  rest:     { normal: '#9ca3af', elevated: '#d1d5db', alert: '#ef4444' },
  toilet:   { normal: '#92400e', elevated: '#b45309', alert: '#ef4444' },
  unknown:  { normal: '#a855f7', elevated: '#c084fc', alert: '#ef4444' },
};

// ─── Alert Threshold Types ──────────────────────────────────────────────────────

export type AlertSeverity = 'warning' | 'critical';

/** Time unit for frequency window — minutes, hours, or days. */
export type FrequencyTimeUnit = 'min' | 'hr' | 'day';

/** Frequency window: a numeric value paired with a time unit.
 *  E.g. { value: 5, unit: 'min' } means "per 5 minutes".
 *  E.g. { value: 1, unit: 'hr' } means "per 1 hour" (the default).
 *  E.g. { value: 1, unit: 'day' } means "per 1 day" (24 hours).
 */
export interface FrequencyWindow {
  value: number;
  unit: FrequencyTimeUnit;
}

/** Default frequency window: 1 hour (backward compatible with existing configs). */
export const DEFAULT_FREQUENCY_WINDOW: FrequencyWindow = { value: 1, unit: 'hr' };

/** Convert a FrequencyWindow to minutes. Returns 60 (1 hour) for undefined. */
export function windowToMinutes(window?: FrequencyWindow): number {
  const w = window ?? DEFAULT_FREQUENCY_WINDOW;
  if (w.unit === 'min') return w.value;
  if (w.unit === 'day') return w.value * 1440;
  return w.value * 60; // hr
}

/** Format a frequency threshold with its window for display.
 *  E.g. fmtFreqLabel(30) → "30/hr"
 *  E.g. fmtFreqLabel(5, { value: 5, unit: 'min' }) → "5/5min"
 *  E.g. fmtFreqLabel(3, { value: 2, unit: 'hr' }) → "3/2hr"
 *  E.g. fmtFreqLabel(5, { value: 1, unit: 'day' }) → "5/day"
 *  E.g. fmtFreqLabel(2, { value: 3, unit: 'day' }) → "2/3days"
 */
export function fmtFreqLabel(freq: number | undefined, window?: FrequencyWindow): string {
  if (freq == null) return '—';
  const w = window ?? DEFAULT_FREQUENCY_WINDOW;
  const unitLabel = formatWindowUnitLabel(w);
  return `${freq}/${unitLabel}`;
}

/** Format just the frequency unit for display.
 *  E.g. fmtFreqUnit() → "/hr"
 *  E.g. fmtFreqUnit({ value: 5, unit: 'min' }) → "/5min"
 *  E.g. fmtFreqUnit({ value: 2, unit: 'hr' }) → "/2hr"
 *  E.g. fmtFreqUnit({ value: 1, unit: 'day' }) → "/day"
 *  E.g. fmtFreqUnit({ value: 3, unit: 'day' }) → "/3days"
 */
export function fmtFreqUnit(window?: FrequencyWindow): string {
  const w = window ?? DEFAULT_FREQUENCY_WINDOW;
  return `/${formatWindowUnitLabel(w)}`;
}

/** Format the window portion of a frequency label (without the leading slash).
 *  E.g. { value: 1, unit: 'hr' } → "hr"
 *  E.g. { value: 5, unit: 'min' } → "5min"
 *  E.g. { value: 1, unit: 'day' } → "day"
 *  E.g. { value: 3, unit: 'day' } → "3days"
 */
export function formatWindowUnitLabel(w: FrequencyWindow): string {
  if (w.unit === 'day' && w.value !== 1) return `${w.value}days`;
  return w.value === 1 ? w.unit : `${w.value}${w.unit}`;
}

/**
 * Convert a frequency threshold (events per frequencyWindow) to events per targetMinutes.
 * Used to normalize threshold values to a chart's y-axis unit.
 * E.g. frequencyToRate(30, { value: 1, unit: 'hr' }, 60) → 30  (30/hr on per-hour chart)
 * E.g. frequencyToRate(30, { value: 1, unit: 'hr' }, 1440) → 720  (30/hr on per-day chart)
 * E.g. frequencyToRate(5, { value: 5, unit: 'min' }, 60) → 60  (5/5min on per-hour chart)
 */
export function frequencyToRate(
  freq: number,
  window: FrequencyWindow | undefined,
  targetMinutes: number,
): number {
  const windowMin = windowToMinutes(window);
  if (windowMin <= 0) return 0;
  return freq * (targetMinutes / windowMin);
}

/** Threshold level with optional frequency (event count within frequencyWindow) and duration (minutes) metrics. */
export interface ThresholdLevel {
  frequency?: number; // event count within frequencyWindow (undefined = not monitoring)
  frequencyWindow?: FrequencyWindow; // time window for frequency (default: 1 hour)
  duration?: number;  // minutes (undefined = not monitoring)
}

export interface BehaviorThreshold {
  enabled: boolean;
  /** Warning-level thresholds (lower urgency). If undefined, no warning alerts for this behavior. */
  warning?: ThresholdLevel;
  /** Critical-level thresholds (higher urgency). If undefined, no critical alerts for this behavior. */
  critical?: ThresholdLevel;
  /** Per-behavior merge gap in seconds. If undefined, falls back to ThresholdConfig.mergeGapSec. */
  mergeGapSec?: number;
}

// Per-behavior threshold configuration
// DB-backed behaviors: sitting, standing, lying, walking, scratching
// Derived behaviors (no DB mapping): spin, toilet, rest

export type AlertBehaviorKey = 'sitting' | 'standing' | 'lying' | 'walking' | 'scratching' | 'spin' | 'toilet' | 'rest';

export interface ThresholdConfig {
  sitting: BehaviorThreshold;
  standing: BehaviorThreshold;
  lying: BehaviorThreshold;
  walking: BehaviorThreshold;
  scratching: BehaviorThreshold;
  // Derived behaviors — disabled by default, shown with tooltip
  spin: BehaviorThreshold;
  toilet: BehaviorThreshold;
  rest: BehaviorThreshold;
  activityDrop: number; // percentage drop in activity
  mergeGapSec: number;  // seconds — same-behavior bars within this gap are merged on the timeline
}

// Default clinically reasonable thresholds
// Each behavior has warning (lower) and critical (higher) thresholds.
// Frequency = event count within frequencyWindow (default: 1 hour), Duration = minutes of continuous behavior.
export const DEFAULT_THRESHOLDS: ThresholdConfig = {
  sitting:     { enabled: true,  warning: { duration: 60 },   critical: { duration: 120 } },
  standing:    { enabled: true,  warning: { duration: 45 },   critical: { duration: 90 } },
  lying:       { enabled: true,  warning: { duration: 120 },  critical: { duration: 240 } },
  walking:     { enabled: true,  warning: { frequency: 30 },  critical: { frequency: 60 } },
  scratching:  { enabled: true,  warning: { frequency: 15 }, critical: { frequency: 30 } },
  spin:        { enabled: false, warning: { frequency: 3 },   critical: { frequency: 6 } },
  toilet:      { enabled: false, warning: { frequency: 5 },   critical: { frequency: 10 } },
  rest:        { enabled: false, warning: { duration: 60 },   critical: { duration: 120 } },
  activityDrop: 50,
  mergeGapSec: 5,  // 5 seconds — global default for same-behavior merge gap
};

// Clinical meaning descriptions for tooltips
export const BEHAVIOR_CLINICAL_MEANING: Record<AlertBehaviorKey, { frequency?: string; duration?: string }> = {
  sitting:   { duration: 'Prolonged sitting may indicate stiffness or reluctance to move' },
  standing:  { duration: 'Extended standing may indicate discomfort or anxiety' },
  lying:     { duration: 'Excessive lying (>2h) may indicate lethargy, pain, or depression' },
  walking:   { frequency: 'High walking frequency may indicate anxiety or pacing behavior' },
  scratching:{ frequency: 'Excessive scratching may indicate allergies, fleas, or skin irritation' },
  spin:      { frequency: 'Spinning is a compulsive behavior linked to stress or neurological issues' },
  toilet:    { frequency: 'Frequent toilet visits may indicate urinary issues or anxiety' },
  rest:      { duration: 'Excessive rest combined with low activity may indicate fatigue or pain' },
};
